/* InAppHelpChatWidget — floating "Need help?" assistant inside the
 * authenticated app.
 *
 * Different from `SupportChatWidget.js`:
 *  - This one only renders for SIGNED-IN users on dashboard / generator /
 *    posts / calendar / etc.
 *  - Replies are short and ALWAYS surface clickable tutorial cards that
 *    deep-link to /tutorials?t=<id>.
 *  - No email capture (already authed).
 *  - No transcript persistence — every session is fresh, single-purpose
 *    ("how do I X?").
 */
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { HelpCircle, X, Send, Loader2, Sparkles, BookOpen, ArrowRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export const InAppHelpChatWidget = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // { role, text, tutorials? }
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const send = async () => {
    const text = draft.trim();
    if (!text || sending) return;
    setError(null);
    setMessages((prev) => [...prev, { role: 'user', text }]);
    setDraft('');
    setSending(true);
    try {
      const r = await api.post('/help/chat', { message: text });
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          text: r.data?.reply || 'Here are some tutorials that might help.',
          tutorials: r.data?.tutorials || [],
        },
      ]);
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Something went wrong. Please try again.';
      setError(detail);
      setMessages((prev) => prev.slice(0, -1));
      setDraft(text);
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  // Click a tutorial card → navigate to the tutorial deep link and close
  // the widget so the user can read it without the overlay in the way.
  const openTutorial = (url) => {
    navigate(url);
    setOpen(false);
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 z-40 flex items-center gap-2 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-3 shadow-lg shadow-indigo-200/60 transition-all hover:scale-105"
        aria-label="Open in-app help"
        data-testid="inapp-help-launcher"
      >
        <HelpCircle className="w-5 h-5" />
        <span className="text-sm font-semibold hidden sm:inline">Need help?</span>
      </button>
    );
  }

  return (
    <div
      className="fixed bottom-5 right-5 z-50 w-[min(92vw,400px)] flex flex-col rounded-xl bg-white border border-zinc-200 shadow-2xl overflow-hidden"
      data-testid="inapp-help-widget"
    >
      {/* Header */}
      <div className="flex items-center justify-between bg-gradient-to-r from-indigo-600 to-violet-600 text-white px-4 py-3">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4" />
          <div>
            <div className="text-sm font-semibold leading-tight">In-app Help</div>
            <div className="text-[10px] text-indigo-100">
              Ask a question, jump to the right tutorial
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="rounded-md p-1 hover:bg-white/10"
          aria-label="Close help"
          data-testid="inapp-help-close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        className="px-4 py-3 h-[420px] overflow-y-auto bg-zinc-50 text-sm space-y-3"
        data-testid="inapp-help-messages"
      >
        {messages.length === 0 && !sending && (
          <div className="space-y-2 text-zinc-600 text-xs mt-4">
            <p className="text-center">
              Hi 👋 Ask me a question and I'll pull up the matching tutorial. Try:
            </p>
            <div className="grid gap-1.5 mt-3">
              {[
                'How do I connect Facebook?',
                'How does scheduling work?',
                'How do I cancel my plan?',
                'What is the AI Post Manager?',
              ].map((q) => (
                <button
                  key={q}
                  type="button"
                  onClick={() => setDraft(q)}
                  className="text-left rounded-md border border-zinc-200 bg-white hover:bg-indigo-50 hover:border-indigo-300 px-3 py-1.5 text-xs text-zinc-700 transition-colors"
                  data-testid="inapp-help-suggested-q"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i}>
            <div
              className={m.role === 'user' ? 'flex justify-end' : 'flex justify-start'}
              data-testid={`inapp-help-msg-${m.role}-${i}`}
            >
              <div
                className={`max-w-[85%] rounded-lg px-3 py-2 whitespace-pre-wrap break-words ${
                  m.role === 'user'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white border border-zinc-200 text-zinc-800'
                }`}
              >
                {m.text}
              </div>
            </div>
            {/* Tutorial cards under assistant replies */}
            {m.role === 'assistant' && m.tutorials?.length > 0 && (
              <div className="mt-2 space-y-1.5 ml-1">
                {m.tutorials.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => openTutorial(t.url)}
                    className="group w-full text-left flex items-start gap-2.5 rounded-lg border border-indigo-200 bg-indigo-50 hover:bg-indigo-100 px-3 py-2 transition-colors"
                    data-testid={`inapp-help-tutorial-${t.id}`}
                  >
                    <div className="rounded-md bg-white border border-indigo-200 p-1.5 mt-0.5 flex-shrink-0">
                      <BookOpen className="w-3 h-3 text-indigo-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-semibold text-indigo-900 truncate">
                          {t.title}
                        </span>
                        <ArrowRight className="w-3 h-3 text-indigo-600 flex-shrink-0 group-hover:translate-x-0.5 transition-transform" />
                      </div>
                      <p className="text-[11px] text-indigo-800/70 mt-0.5 leading-snug line-clamp-2">
                        {t.summary}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="bg-white border border-zinc-200 rounded-lg px-3 py-2 text-zinc-500 text-xs flex items-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin" /> Looking up…
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="border-t border-zinc-200 bg-white p-3 space-y-2">
        {error && (
          <div className="text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-2 py-1.5">
            {error}
          </div>
        )}
        <div className="flex gap-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKey}
            rows={1}
            placeholder="Ask a question…"
            className="flex-1 px-3 py-2 text-sm rounded-md border border-zinc-300 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 resize-none max-h-32"
            data-testid="inapp-help-input"
          />
          <button
            type="button"
            onClick={send}
            disabled={sending || !draft.trim()}
            className="rounded-md bg-indigo-600 hover:bg-indigo-700 disabled:bg-zinc-300 text-white px-3 flex items-center justify-center transition-colors"
            aria-label="Send"
            data-testid="inapp-help-send"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
        <p className="text-[10px] text-zinc-400 text-center">
          Replies link to step-by-step tutorials. Need a human? Open Support in the sidebar.
        </p>
      </div>
    </div>
  );
};
